import Pica from "pica";
import LoadImage from "blueimp-load-image";
import {Enums} from "../../thor.enums";
import {Constants} from "../../thor.const";

export class ImageService {
    public static $inject: string[] = [
        "$q",
        "$log"
    ];

    private $q;
    private $log;
    private hasBrowserAutomaticOrientationSupport;

    constructor(
        $q: ng.IQService,
        $log: ng.ILogService
    ) {
        this.$q = $q;
        this.$log = $log;

        // Hack from https://github.com/blueimp/JavaScript-Load-Image/blob/83ace3e/js/load-image-orientation.js#L52
        // black 2x1 JPEG, with the following meta information set:
        // - EXIF Orientation: 6 (Rotated 90° CCW)
        const testImageURL =
            "data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA" +
            "AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA" +
            "QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE" +
            "BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAEAAgMBEQACEQEDEQH/x" +
            "ABKAAEAAAAAAAAAAAAAAAAAAAALEAEAAAAAAAAAAAAAAAAAAAAAAQEAAAAAAAAAAAAAAAA" +
            "AAAAAEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwA/8H//2Q==";
        const img = document.createElement("img");
        img.onload = () => {
            // Check if browser supports automatic image orientation:
            this.hasBrowserAutomaticOrientationSupport = img.width === 1 && img.height === 2;
        };
        img.src = testImageURL;
    }

    /**
     * Resize an image using canvas & pica
     * Get your ImageQuality settings to get the max resolution
     * Resize the image by keeping the resolution ratio and return a blob
     * Remove EXIF information from file if exists and keep original orientation
     * @param { File | { blob: Blob, dataURI: string } } data
     * @param { boolean } asDataURI
     * @return { Promise<string | Blob> }
     */
    public resizeImage(data: File | { blob: Blob; dataURI: string }, asDataURI?: boolean) {
        const deferred = this.$q.defer();
        const imageQuality = <Enums.ImageQuality>(JSON.parse(localStorage.getItem("settings"))).imageQuality;
        const maxRes = Constants.IMAGE_RES[imageQuality];
        const image = new Image();
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        let exifOrientation = 1;
        let file;

        if (data instanceof File) {
            file = data;
            this.$q.all([
                this._getDataUrl(file),
                this._getExifOrientation(file)
                    .then(orientation => exifOrientation = orientation)
            ]).then(results => image.src = results[0]);
        } else {
            file = data.blob;
            this._getExifOrientation(file)
                .then(orientation => {
                    exifOrientation = orientation;
                    image.src = data.dataURI;
                });
        }

        image.onload = () => {
            let height = 0;
            let width = 0;

            if (image.width > maxRes.width || image.height > maxRes.height) {
                const ratio = Math.min(maxRes.width / image.width, maxRes.height / image.height);

                height = image.height * ratio;
                width = image.width * ratio;
            } else {
                height = image.height;
                width = image.width;
            }

            canvas.height = height;
            canvas.width = width;

            const pica = new Pica();

            if (this.hasBrowserAutomaticOrientationSupport) {
                context.drawImage(image, 0, 0, image.width, image.height, 0, 0, width, height);
                deferred.resolve(this._format(canvas, asDataURI));
            } else {
                context.drawImage(image, 0, 0);
                pica.resize(
                    image,
                    canvas,
                    {}
                )
                    .then(resizedCanvas => {
                        const localSettings = JSON.parse(localStorage.getItem("settings"));
                        if (localSettings.handleExif === true) {
                            this._rotateImageWithExif(resizedCanvas, exifOrientation, width, height)
                                .then(rotated => deferred.resolve(this._format(rotated, asDataURI)));
                        } else {
                            deferred.resolve(this._format(canvas, asDataURI));
                        }
                    });
            }

        };

        image.onerror = error => {
            this.$log.warn("An error occured while loading image.", error);
            deferred.reject(error);
        };

        return deferred.promise;
    }

    private _format(canvas: HTMLCanvasElement, asDataURI: boolean) {
        if (asDataURI) {
            return canvas.toDataURL("image/jpeg", 0.9);
        }
        const pica = new Pica();

        return pica.toBlob(canvas, "image/jpeg", 0.9);
    }

    private _getDataUrl(file: File) {
        const deferred = this.$q.defer();

        const fileLoader = new FileReader();

        fileLoader.readAsDataURL(file);

        fileLoader.onload = () => deferred.resolve(fileLoader.result);

        fileLoader.onerror = error => {
            this.$log.warn("An error occured while reading the file.", error);
            deferred.reject(error);
        };
        return deferred.promise;
    }

    private _getExifOrientation(file: File | Blob) {
        const deferred = this.$q.defer();

        LoadImage.parseMetaData(
            file,
            metaData => deferred.resolve(metaData.exif ? metaData.exif.get("Orientation") : undefined),
            { disableImageHead: true }
        );

        return deferred.promise;
    }

    private _rotateImageWithExif(canvas: HTMLCanvasElement, exifOrientation: number, width: number, height: number) {
        const deferred = this.$q.defer();

        if (exifOrientation !== 1) {
            const resizedImage = new Image();
            const context = canvas.getContext("2d");
            resizedImage.src = canvas.toDataURL("image/jpeg", 0.9);

            resizedImage.onload = () => {
                if (4 < exifOrientation && exifOrientation < 9) {
                    canvas.width = height;
                    canvas.height = width;
                } else {
                    canvas.width = width;
                    canvas.height = height;
                }

                switch (exifOrientation) {
                    case 2: context.transform(-1, 0, 0, 1, width, 0); break;
                    case 3: context.transform(-1, 0, 0, -1, width, height ); break;
                    case 4: context.transform(1, 0, 0, -1, 0, height ); break;
                    case 5: context.transform(0, 1, 1, 0, 0, 0); break;
                    case 6: context.transform(0, 1, -1, 0, height , 0); break;
                    case 7: context.transform(0, -1, -1, 0, height , width); break;
                    case 8: context.transform(0, -1, 1, 0, 0, width); break;
                    default: break;
                }

                context.drawImage(resizedImage, 0, 0);
                deferred.resolve(canvas);
            };

            resizedImage.onerror = error => {
                this.$log.warn("An error occured while loading resized image.", error);
                deferred.reject(error);
            };
        } else {
            deferred.resolve(canvas);
        }

        return deferred.promise;
    }
}
